import { Div, Icon, Label, useWindowSize } from "@grassrootly/rn-core";
import { ScreenLayout } from "../../layout/components";
import { MainStackScreenProps } from "../../navigation/Navigation.types";
import { Image, Text } from "react-native";
import { ScrollView } from 'react-native-gesture-handler';
import { images } from "../../layout/assets";
import { GetPlayersTotals, PlayerTotals, GetPlayerById, PlayerMinimal, GetPlayersGroupsExperience, PlayerGroupExperience } from "@grassrootly/api";
import useBaseRequest from "../../api/BaseRequest";
import { useAuth } from "../../components/guard/useAuth";
import { useEffect, useState } from "react";
import moment from "moment";
import { PLAYER_TABS, useEqipaCard } from "./useEqipaCard";
import PlayerTab from "./PlayerTab";
import PlayerStats from "./tabs/PlayerStats";
import PlayerMatches from "./tabs/PlayerMatches";
import PlayerGroups from "./tabs/PlayerGroups";

export default function EqipaCardScreen({ navigation, route }: MainStackScreenProps<"eqipa-card">) {
  // Player minimal info (name, profile picture)
  const [playerInfo, setPlayerInfo] = useState<PlayerMinimal | null>(null);
  // Auth user
  const { user } = useAuth();
  // Tab management
  const { selectedTab, playerTabs, onTabSelected } = useEqipaCard({ navigation, route });
  // Totals state
  const [totals, setTotals] = useState<PlayerTotals | null>(null);
  // Groups with experience
  const [groups, setGroups] = useState<PlayerGroupExperience[]>([]);

  // Fetch groups with experience points
  const { execute: getGroups } = useBaseRequest(async (params: { pid: number }) => {
    return GetPlayersGroupsExperience(params);
  }, {
    onCompleted: (data: PlayerGroupExperience[] | null) => {
      setGroups(data || []);
    }
  });


  useEffect(() => {
    const effectiveId = route?.params?.playerId ?? user?.id;
    if (!effectiveId) return;
    // fetch totals
    getTotals({ pid: effectiveId });
    // fetch player basic info
    getPlayer({ id: effectiveId });
    // fetch groups experience
    getGroups({ pid: effectiveId });
  }, [user?.id, route?.params?.playerId]);

  // Fetch player totals (W/D/L, goals, assists)
  const { execute: getTotals } = useBaseRequest(async (params: { pid: number }) => {
    return GetPlayersTotals(params);
  }, {
    onCompleted: (data: PlayerTotals | null) => {
      setTotals(data);
    }
  });

  // Fetch player minimal info (name, profile_picture)
  const { execute: getPlayer } = useBaseRequest(async (params: { id: number }) => {
    return GetPlayerById(params);
  }, {
    onCompleted: (data: PlayerMinimal | null) => {
      setPlayerInfo(data);
    }
  });

  return (
    <ScreenLayout
      header={{
        center: "Player Profile",
        left: <Icon name="menu" size={24} onPress={navigation.toggleDrawer} />,
      }}
      centerContent={false}
    >
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingTop: 16, paddingBottom: 160 }}
        showsVerticalScrollIndicator={true}
      >
        {/* Dominant profile picture */}
        <Image
          style={{ height: 200, width: 200, borderRadius: 200, marginLeft: "auto", marginRight: "auto" }}
          resizeMode="cover"
          source={playerInfo?.profile_picture ? { uri: playerInfo.profile_picture } : images.playerAvatar}
        />

        {/* Player name emphasized, EXP beneath */}
        <Div classNames={["mt-{16}", "alignItemsCenter"]}>
          <Label type="l1Bold">{playerInfo?.name || "Player"}</Label>
          <Text
            style={{
              marginTop: 4,
              color: "#1ABC6C",
              fontWeight: "800",
              fontSize: 16,
            }}
          >
            {totals?.total_experience_points ?? 0} EXP
          </Text>
          <Label type="l3Regular" classNames={["mt-{6}"]}>
            {totals?.total_games ?? 0} matches played
          </Label>
        </Div>

        {/* PLAYER TABS */}
        <Div classNames={["mt-{24}", "mb-{20}"]}>
          <Div classNames={["flexRow"]}>
            {playerTabs.map((tab) => (
              <PlayerTab
                key={tab.alias}
                alias={tab.alias}
                isActive={selectedTab === tab.alias}
                title={tab.title}
                onPress={onTabSelected}
              />
            ))}
          </Div>
        </Div>

        {/* TAB CONTENT */}
        <Div classNames={["w100", "mt-{16}"]}>
          {selectedTab === PLAYER_TABS.stats.alias && (
            <PlayerStats totals={totals} />
          )}
          {selectedTab === PLAYER_TABS.matches.alias && (route?.params?.playerId ?? user?.id) && (
            <PlayerMatches playerId={route?.params?.playerId ?? user?.id!} navigation={navigation} />
          )}
          {selectedTab === PLAYER_TABS.groups.alias && (
            <PlayerGroups groups={groups} navigation={navigation} />
          )}
        </Div>

        
      </ScrollView>
    </ScreenLayout>
  );
}
